import { useEffect, useRef } from 'react';

import type { ChatMessage } from '@karehero/models';

const KEY_SEPARATOR = '\n';

/** The messages said from our side, Keni's or the server's. */
export const saidKeys = (messages: readonly ChatMessage[]) =>
  messages.filter((m) => m.from === 'system').map((m) => m.id);

/**
 * Felt whenever a key appears that was not there before; those there once
 * ready and settled are not new. Once settled it stays so until ready again,
 * as settling is only waited for at the start.
 */
export const useFeltOnNew = (
  keys: readonly string[],
  { isReady, isSettled }: { isReady: boolean; isSettled: boolean },
  feel: () => void,
) => {
  const seen = useRef<ReadonlySet<string>>(undefined);
  const joined = keys.join(KEY_SEPARATOR);
  useEffect(() => {
    if (!isReady) {
      seen.current = undefined;
      return;
    }
    if (!seen.current && !isSettled) return;
    const current = joined ? joined.split(KEY_SEPARATOR) : [];
    const before = seen.current;
    seen.current = new Set(current);
    if (before && current.some((key) => !before.has(key))) feel();
  }, [feel, isReady, isSettled, joined]);
};
